import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, Pencil } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";
import { useIsSuperAdmin } from "@/lib/placement";
import TriagePanel from "@/components/nds/impact/TriagePanel";
import { reApi } from "@/lib/residentApi";

const STEP_MARK = { "05": ["job_signoff", "PMC job sign-off", true], "08": ["career_ready", "Mark career ready", false], "09": ["moving_out", "Mark own place / leadership", false] };

function PersonActions({ step, p, pmc, onChanged, onTriage }) {
  if (p.kind === "referral") return <button type="button" onClick={() => onTriage(p)} data-testid={`impact-triage-${p.id}`} className="shrink-0 px-2 py-1 border border-amber-400/60 text-amber-200 rounded-sm font-mono text-[9px] tracking-[0.16em] uppercase">Triage{p.triage?.length ? ` · ${p.triage.length}/3` : ""}</button>;
  const m = STEP_MARK[step];
  const marks = Object.entries(p.marks || {});
  const act = async (fn) => { try { await fn(); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {m && (!m[2] || pmc) && <button type="button" onClick={() => act(() => reApi.post(`/impact-flow/residents/${p.id}/mark/${m[0]}`, { note: "" }))} data-testid={`impact-mark-${m[0]}-${p.id}`}
        className="px-2 py-1 border border-emerald-400/60 text-emerald-200 rounded-sm font-mono text-[9px] tracking-[0.16em] uppercase">{m[1]}</button>}
      {marks.map(([k, v]) => <span key={k} className="text-[10px] text-slate-400" data-testid={`impact-markchip-${k}-${p.id}`}>{k.replace("_", " ")} · {v.by?.name}
        <button type="button" onClick={() => act(() => reApi.del(`/impact-flow/residents/${p.id}/mark/${k}`))} data-testid={`impact-unmark-${k}-${p.id}`} className="ml-1 text-red-300 hover:underline">undo</button></span>)}
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api/re/impact-flow`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` } });
const usd = (n) => (n == null ? "—" : `$${Math.round(n).toLocaleString()}`);

function Stat({ label, value, testid }) {
  return <div className="nds-card px-4 py-3" data-testid={testid}><div className="nds-label-dim">{label}</div><div className="mt-1 text-xl font-serif text-nex-silver">{value}</div></div>;
}

function CostEditor({ value, onSaved }) {
  const [v, setV] = useState(value ?? "");
  const [open, setOpen] = useState(false);
  const save = async () => {
    try { await axios.put(`${API}/daily-cost`, { daily_cost: Number(v) }, auth()); toast.success("Cost to serve updated"); setOpen(false); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} data-testid="impact-cost-edit" className="inline-flex items-center gap-1.5 font-mono text-[0.65rem] tracking-[0.2em] uppercase text-nex-gold hover:underline"><Pencil className="h-3 w-3" />{value == null ? "Set daily cost to serve" : `Daily cost ${usd(value)} · edit`}</button>;
  return (
    <div className="flex items-center gap-2">
      <span className="text-nex-silver/70 text-sm">$ per resident per day</span>
      <input type="number" min="0" step="1" value={v} onChange={(e) => setV(e.target.value)} data-testid="impact-cost-input" className="w-24 bg-black/30 border border-white/15 px-2 py-1 text-nex-silver text-sm" />
      <button type="button" onClick={save} disabled={v === ""} data-testid="impact-cost-save" className="px-3 py-1 border border-nex-gold/50 text-nex-gold font-mono text-[0.65rem] uppercase tracking-[0.2em] disabled:opacity-40">Save</button>
    </div>
  );
}

function StepCard({ s, onOpen }) {
  return (
    <button type="button" onClick={onOpen} className="nds-step w-full text-left" data-testid={`impact-step-${s.id}`} title={s.about}>
      <span className="nds-step__bar" />
      <span className="nds-step__num">STEP {s.id}</span>
      <div className="font-serif text-[1.05rem] leading-snug font-semibold pr-8 mt-6">{s.title}</div>
      <div className="mt-4 flex items-center gap-2">
        <span className={`nds-pill ${s.count ? "nds-pill--gold" : "nds-pill--live"}`} style={{ padding: "3px 8px", fontSize: "0.6rem" }} data-testid={`impact-step-count-${s.id}`}>{s.count} {s.count === 1 ? "person" : "people"}</span>
        <ChevronRight className="w-4 h-4 ml-auto opacity-40" />
      </div>
      <div className="mt-2 font-mono text-[0.6rem] tracking-[0.12em] uppercase opacity-60" data-testid={`impact-step-money-${s.id}`}>
        {s.cost != null && <>Cost {usd(s.cost)} · </>}Earned {usd(s.earned)} · Saved {usd(s.saved)}
      </div>
    </button>
  );
}

function StepPeople({ s, onClose, pmc, onChanged }) {
  const [tri, setTri] = useState(null);
  useEffect(() => { setTri(null); }, [s?.id]);
  return (
    <Sheet open={!!s} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-[#0b0f1f] text-slate-100 border-white/10 w-full sm:max-w-lg overflow-y-auto" data-testid="impact-step-sheet">
        {s && tri && <TriagePanel referral={tri} onBack={() => setTri(null)} onChanged={onChanged} />}
        {s && !tri && <>
          <SheetHeader><SheetTitle className="text-slate-100 font-serif">Step {s.id} · {s.title}</SheetTitle><SheetDescription className="text-slate-400">{s.about}</SheetDescription></SheetHeader>
          <ul className="mt-5 divide-y divide-white/10">
            {s.people.map((p) => (
              <li key={p.id} className="py-3 flex flex-wrap items-start gap-x-3 gap-y-2" data-testid={`impact-person-${p.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-slate-400">{p.days != null && `${p.days} days in program`}{p.days != null && p.next && " · "}{p.next && `Next: ${p.next}`}</div>
                  {p.kind === "resident" && <div className="text-[11px] text-slate-500 mt-0.5">{p.cost != null && `Cost ${usd(p.cost)} · `}Earned {usd(p.earned)} · Saved {usd(p.saved)}</div>}
                </div>
                <PersonActions step={s.id} p={p} pmc={pmc} onChanged={onChanged} onTriage={setTri} />
                {p.kind === "resident" && <OpenResidentButton residentId={p.id} name={p.name} onOpened={onClose} />}
              </li>
            ))}
            {!s.people.length && <li className="py-6 text-sm text-slate-400" data-testid="impact-step-empty">No one is on this step right now.</li>}
          </ul>
        </>}
      </SheetContent>
    </Sheet>
  );
}

/** TEA's 10-step impact flow: every person placed on a step from their own records, with cost to serve and resident earnings. */
export default function ImpactFlow() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const sa = useIsSuperAdmin();
  const load = useCallback(() => axios.get(API, auth()).then((r) => setD(r.data)).catch(() => setD({ error: true })), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="nds-card p-8 text-center font-mono text-sm" data-testid="impact-loading">Loading flow…</div>;
  if (d.error) return <div className="nds-card p-8 text-center font-mono text-sm text-red-300" data-testid="impact-error">Couldn't load the impact flow.</div>;
  const m = d.summary;
  return (
    <section data-testid="impact-flow" className="flex flex-col gap-5">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="flex flex-col gap-2">
          <div className="nds-label-dim">// 10-STEP IMPACT FLOW · RESIDENT LIFECYCLE</div>
          <h2 className="text-nex-silver text-2xl md:text-3xl font-serif tracking-tight">Every person. Every step. Every dollar.</h2>
        </div>
        {sa ? <CostEditor value={d.daily_cost} onSaved={load} /> : <span className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-nex-silver/50">Daily cost to serve {usd(d.daily_cost)}</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="impact-summary">
        <Stat label="In program" value={m.in_program} testid="impact-sum-in-program" />
        <Stat label="Referrals waiting" value={m.referrals} testid="impact-sum-referrals" />
        <Stat label="Graduates" value={m.graduates} testid="impact-sum-graduates" />
        <Stat label="Exited early" value={m.exited_early} testid="impact-sum-exited" />
        <Stat label="Residents earned" value={usd(m.earned)} testid="impact-sum-earned" />
        <Stat label="Residents saved" value={usd(m.saved)} testid="impact-sum-saved" />
        <Stat label="Avg cost per person" value={usd(m.avg_cost)} testid="impact-sum-avg-cost" />
        <Stat label="Cost per graduate" value={usd(m.cost_per_graduate)} testid="impact-sum-cost-grad" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {d.steps.map((s) => <StepCard key={s.id} s={s} onOpen={() => setOpen(s)} />)}
      </div>
      <StepPeople s={open && d.steps.find((x) => x.id === open.id)} onClose={() => setOpen(null)} pmc={d.me_pmc} onChanged={load} />
    </section>
  );
}
